import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

type VerificationStatus =
  | 'verified'
  | 'invalid_signature'
  | 'not_configured'
  | 'unmapped'
  | 'missing_signature'
  | 'malformed';

interface HealthResponse {
  success: boolean;
  data: {
    windowHours: number;
    counts: Record<VerificationStatus, number>;
    unmappedCount: number;
    lastFailure?: {
      gateway: 'stripe' | 'razorpay';
      eventType: string;
      status: VerificationStatus;
      receivedAt: string;
    };
    needsAttention: boolean;
  };
}

const HINTS: Record<VerificationStatus, { title: string; action: string }> = {
  verified: { title: '', action: '' },
  invalid_signature: {
    title: 'Payment webhook signatures are failing verification',
    action:
      "Double-check the webhook signing secret in the Payment tab below matches the one shown in your Razorpay/Stripe dashboard. A common mix-up is pasting the live secret into the test-mode field (or vice versa). Until this is fixed, payments will not move off 'pending'.",
  },
  not_configured: {
    title: 'Payment webhook secret is missing',
    action:
      'Add your Razorpay/Stripe webhook signing secret in the Payment tab below. Without it, Tyashin rejects incoming webhooks and orders stay in pending_payment.',
  },
  unmapped: {
    title: 'Received webhooks we could not attach to any order',
    action:
      'Usually fine if you triggered a test payment directly from the gateway dashboard (not through the storefront). If these keep appearing from real customer payments, something is off with how the checkout flow creates gateway orders — reach out to support.',
  },
  missing_signature: {
    title: 'Received webhook deliveries without a signature',
    action:
      'Check that the webhook URL configured in the gateway dashboard is exactly `/api/v1/public/ecommerce/webhooks/<gateway>`. Unsigned probes to other paths can be ignored.',
  },
  malformed: {
    title: 'Received webhooks with invalid JSON',
    action:
      'Likely a broken proxy between the gateway and Tyashin. If this persists, share a sample payload with support.',
  },
};

interface Props {
  projectId: string;
}

export function WebhookHealthBanner({ projectId }: Props) {
  const { data } = useQuery({
    queryKey: ['webhook-health', projectId],
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/ecommerce/webhook-health?windowHours=24`);
      return res.data as HealthResponse;
    },
    enabled: Boolean(projectId),
    // Recompute every 60s while settings page is open so fixes reflect quickly.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const health = data?.data;
  if (!health?.needsAttention) return null;

  const last = health.lastFailure;
  const hint = last ? HINTS[last.status] : null;

  // Severity: signature/secret issues are blocking (red); unmapped/missing-sig/malformed are yellow.
  const isBlocking = last?.status === 'invalid_signature' || last?.status === 'not_configured';

  const wrapperClass = isBlocking
    ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40'
    : 'border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40';
  const iconClass = isBlocking
    ? 'text-red-600 dark:text-red-400'
    : 'text-amber-600 dark:text-amber-400';
  const titleClass = isBlocking
    ? 'text-red-900 dark:text-red-100'
    : 'text-amber-900 dark:text-amber-100';

  return (
    <Card className={wrapperClass}>
      <CardContent className="pb-4 pt-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className={`mt-0.5 h-5 w-5 shrink-0 ${iconClass}`} />
          <div className="flex-1 space-y-2">
            <div className={`text-sm font-medium ${titleClass}`}>
              {hint?.title || 'Some payment webhook deliveries need attention'}
            </div>
            {hint?.action && <p className="text-muted-foreground text-xs">{hint.action}</p>}
            <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {health.counts.invalid_signature > 0 && (
                <span>{health.counts.invalid_signature} invalid signature</span>
              )}
              {health.counts.not_configured > 0 && (
                <span>{health.counts.not_configured} secret missing</span>
              )}
              {health.counts.unmapped > 0 && <span>{health.counts.unmapped} unmatched</span>}
              {health.counts.missing_signature > 0 && (
                <span>{health.counts.missing_signature} no signature</span>
              )}
              {health.counts.malformed > 0 && <span>{health.counts.malformed} malformed</span>}
              <span className="opacity-70">· last {health.windowHours}h</span>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Link to={`/projects/${projectId}/orders`}>
                <Button size="sm" variant="outline">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  Review affected orders
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
